import styles from "./money-market.module.css"
import React from "react"
import assign from "object-assign"
import * as finance from "shared/finance"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import {DoubleBar} from "story/components/graphs/bar"
import * as graphUtil from "shared/graphs/util"
import AccessibilityTable from "activities/components/shared/accessibility-table"

/* Data for graph format:
[
[year, deposit, totalContribution, interest],
[year, deposit, totalContribution, interest],
[year, deposit, totalContribution, interest],
]
*/

const getTotalContribution = (contribution, years) => (contribution > 0 ? contribution * (years * 12) : 0)

const getInterestEarned = (data) => {
  const last = data[data.length - 1]
  return last[last.length - 1]
}

const genData = (initial, contribution, years, rate) =>
  util.xAxisArray(years).reduce((memo, years, idx) => {
    memo.push([years, initial, getTotalContribution(contribution, years), util.round(finance.getCompoundInterest(initial - contribution, contribution, years, rate))])
    return memo
  }, [])

const calculate = ({initial, contribution, years, apy}) => {
  const graphData = genData(initial, contribution, years, convertAPYtoRate(apy))
  const totalContributions = getTotalContribution(contribution, years) + initial
  const interestEarned = getInterestEarned(graphData)
  return {
    data: graphData /*data for graph*/,
    totalContributions: totalContributions /*total contribution made including initial*/,
    interestEarned: interestEarned /*total interest earned for the life of the account*/,
    totalBalance: totalContributions + interestEarned /*total account balance*/
  }
}

/* Below is the formula used to convert APY to an interest rate */
// (APY + 1)^(1/Periods in a Year) – 1 = PeriodicRate
const convertAPYtoRate = (apy) => (Math.pow(apy / 100 + 1, 1 / 12) - 1) * 12

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {initial, contribution, years, apy, resetKey} = state
    const {id, theme} = getProps()

    const {data, totalContributions, interestEarned, totalBalance} = calculate(state)

    const accessibilityTableHeadings = [
      {
        name: "Year",
        key: "year"
      },
      {
        name: "Initial Deposit",
        key: "initial"
      },
      {
        name: "Contributions Made",
        key: "contribution"
      },
      {
        name: "Interest Earned",
        key: "interest"
      }
    ]

    const accessibilityTableData = data.map((row, idx) => ({
      year: idx,
      initial: util.formatDollarsNoCents(row[1]),
      contribution: util.formatDollarsNoCents(row[2]),
      interest: util.formatDollarsNoCents(row[3])
    }))

    const accessibilityTableLabel = "Money Market Account Summary"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer>
              <inputs.CalcInput id={id} title="Initial Deposit" onChange={updateState("initial", util.fromString)} initialValue={initial} resetKey={resetKey} />
              <inputs.Input id={id} title="APY" onChange={updateState("apy")} value={apy} valueType="percent" />
              <inputs.RangeInput
                id={id}
                title="Monthly Contribution"
                onChange={updateState("contribution", util.fromString)}
                initialValue={contribution}
                min={0}
                max={3000}
                step={100}
                units="$"
                resetKey={resetKey}
              />
              <inputs.RangeInput
                id={id}
                title="Years to Save"
                onChange={updateState("years", util.fromString)}
                initialValue={years}
                min={1}
                max={30}
                step={1}
                units="years"
                valueType="years-full"
                resetKey={resetKey}
              />
            </shared.InputsContainer>
            <shared.ChartContainer>
              <DoubleBar
                isStacked={true}
                values={data}
                yAxis="Balance"
                formatter="currency"
                tooltip={[
                  "Year",
                  {name: "Initial Deposit", type: "currency", color: graphUtil.getColor(theme, 2)},
                  {name: "Contributions Made", type: "currency", color: graphUtil.getColor(theme, 1)},
                  {name: "Interest Earned", type: "currency", color: graphUtil.getColor(theme, 4)}
                ]}
                customTooltipStyles={{width: 281, rows: 4}}
                type="calculator"
                colors={[graphUtil.getColor(theme, 2), graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 4)]}
                ariaLabel="This bar graph visually represents how much you can save in a money market account. Read below to find the total balance, interst earned over time, and the total contributions made in the account."
              />
            </shared.ChartContainer>
            <shared.SummaryContainer className={styles["summary"]}>
              <callouts.Callout>
                <callouts.Detail label="Total Balance" result={util.formatDollarsNoCents(totalBalance)} dataTestId="total-balance" />
                <callouts.Detail label="Interest Earned" result={util.formatDollarsNoCents(interestEarned)} dataTestId="interest-earned" />
                <callouts.Detail label="Total Contributions" result={util.formatDollarsNoCents(totalContributions)} dataTestId="contributions" />
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {data, totalContributions, interestEarned, totalBalance} = calculate(state)
      return assign(state, {
        totalBalance,
        interestEarned,
        totalContributions
      })
    },
    defaultProps: {
      id: "money-market-account-calculator",
      className: "savings-calculator",
      title: "Money Market Account Calculator",
      subtitle: "Adjust the inputs to see how much your savings in a money market account grow over time."
    },
    defaultState: {
      initial: 2500,
      contribution: 100,
      years: 10,
      apy: 1.7
    }
  }
)
